import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { exhaustMap, filter, take } from 'rxjs/operators';
import { LoadingSpinnerService } from '@arc/unified-payments-frontend/shared-payment';
import { ActionCreator, Creator } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';

/* istanbul ignore next */
export class BaseEffect {
  constructor(
    protected actions$: Actions,
    protected loadingSpinnerService: LoadingSpinnerService,
  ) {}

  protected loadUntilActionsComplete(
    sourceActionTypes: string[] | ActionCreator<string, Creator>[],
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
  ) {
    return this.actions$.pipe(
      ofType(...sourceActionTypes),
      exhaustMap(() => {
        return this.addLoadingSpinnerSource(completeActionTypes);
      }),
    );
  }

  protected loadUntilActionsCompleteWithFilter(
    sourceActionTypes: string[] | ActionCreator<string, Creator>[],
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
    filterData: Observable<boolean>,
  ) {
    return this.actions$.pipe(
      ofType(...sourceActionTypes),
      concatLatestFrom(() => [filterData]),
      filter(([, data]) => data),
      exhaustMap(() => {
        return this.addLoadingSpinnerSource(completeActionTypes);
      }),
    );
  }

  protected loadUntilActionComplete$(
    sourceActionTypes: string[] | ActionCreator<string, Creator>[],
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
  ) {
    return createEffect(
      () =>
        this.loadUntilActionsComplete(sourceActionTypes, completeActionTypes),
      { dispatch: false },
    );
  }

  protected loadUntilActionCompleteWithFilter$(
    sourceActionTypes: string[] | ActionCreator<string, Creator>[],
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
    filterData: Observable<boolean>,
  ) {
    return createEffect(
      () =>
        this.loadUntilActionsCompleteWithFilter(
          sourceActionTypes,
          completeActionTypes,
          filterData,
        ),
      { dispatch: false },
    );
  }

  protected loadUntilActionsCompleteWithExternalTrigger$(
    source$: Observable<unknown>,
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
  ) {
    return createEffect(
      () =>
        source$.pipe(
          exhaustMap(() => {
            return this.addLoadingSpinnerSource(completeActionTypes);
          }),
        ),
      { dispatch: false },
    );
  }

  protected addLoadingSpinnerSource(
    completeActionTypes: string[] | ActionCreator<string, Creator>[],
  ) {
    const completeActions$ = this.actions$.pipe(
      ofType(...completeActionTypes),
      take(1),
    );
    this.loadingSpinnerService.loadUntil(completeActions$);
    return of();
  }
}
